import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle, Send } from "lucide-react";
import { getErrorMessage } from "@/lib/errorUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmbedReferralForm() {
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") || searchParams.get("utm_campaign") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    company_name: "",
    team_size: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptPrivacy) {
      toast.error("Devi accettare il trattamento dei dati personali");
      return;
    }

    if (!formData.team_size) {
      toast.error("Seleziona il numero di venditori nel team");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/public/referral`, {
        ...formData,
        referral_code: referralCode,
        accept_privacy: acceptPrivacy
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'invio del form"));
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <motion.div
          className="glass-card p-8 max-w-md text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          data-testid="embed-form-success"
        >
          <div className="w-14 h-14 mx-auto rounded-full bg-[#fcb900]/20 flex items-center justify-center mb-4">
            <CheckCircle className="w-7 h-7 text-[#fcb900]" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Richiesta inviata!</h2>
          <p className="text-sm text-muted-foreground">
            Grazie per il tuo interesse. Il team Aiclo ti contatterà a breve per organizzare una demo gratuita e senza impegno.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        className="glass-card p-5 sm:p-6 w-full max-w-md"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="embed-referral-form">
          <div className="space-y-1.5">
            <Label className="text-xs">Nome e Cognome *</Label>
            <Input
              value={formData.contact_name}
              onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
              className="bg-black/20 border-white/10 h-10"
              required
              data-testid="embed-contact-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Email *</Label>
            <Input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              className="bg-black/20 border-white/10 h-10"
              required
              data-testid="embed-contact-email"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Telefono *</Label>
            <Input
              type="tel"
              value={formData.contact_phone}
              onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
              className="bg-black/20 border-white/10 h-10"
              required
              data-testid="embed-contact-phone"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Nome Azienda *</Label>
            <Input
              value={formData.company_name}
              onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
              className="bg-black/20 border-white/10 h-10"
              required
              data-testid="embed-company-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Numero venditori nel team *</Label>
            <Select
              value={formData.team_size}
              onValueChange={(value) => setFormData({ ...formData, team_size: value })}
            >
              <SelectTrigger className="bg-black/20 border-white/10 h-10" data-testid="embed-team-size">
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="1-3">1-3</SelectItem>
                <SelectItem value="4-10">4-10</SelectItem>
                <SelectItem value="11-50">11-50</SelectItem>
                <SelectItem value="51-100">51-100</SelectItem>
                <SelectItem value="100+">100+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-start space-x-3 pt-1">
            <Checkbox
              id="embed_accept_privacy"
              checked={acceptPrivacy}
              onCheckedChange={(checked) => setAcceptPrivacy(checked)}
              data-testid="embed-checkbox-privacy"
            />
            <Label htmlFor="embed_accept_privacy" className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
              Dichiaro di aver letto l'<a href="https://aiclo.io/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-[#fcb900] hover:underline">Informativa sulla Privacy</a> e acconsento al trattamento dei miei dati personali ai sensi del Regolamento UE 2016/679 (GDPR).
            </Label>
          </div>

          <Button
            type="submit"
            className="w-full h-10 rounded-full btn-gold"
            disabled={isLoading || !formData.team_size}
            data-testid="embed-submit-btn"
          >
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            Richiedi Demo Gratuita
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
